import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Ban, Trash2, Search, Download, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Suppression = () => {
  const [suppressed, setSuppressed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [reason, setReason] = useState('manual');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSuppression();
  }, []);

  const fetchSuppression = async () => {
    try {
      const response = await axios.get(`${API_URL}/suppression`);
      setSuppressed(response.data);
    } catch (error) {
      toast.error('Failed to fetch suppression list');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newEmail.trim()) {
      toast.error('Please enter an email');
      return;
    }
    try {
      await axios.post(`${API_URL}/suppression`, { email: newEmail.trim(), reason });
      toast.success('Email added to suppression list');
      setDialogOpen(false);
      setNewEmail('');
      setReason('manual');
      fetchSuppression();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add email');
    }
  };

  const handleRemove = async (id) => {
    try {
      await axios.delete(`${API_URL}/suppression/${id}`);
      toast.success('Removed');
      fetchSuppression();
    } catch (error) {
      toast.error('Failed to remove email');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm(`Are you sure you want to clear ALL ${suppressed.length} suppressed emails? This will also clear bounces from SendGrid so they can be retried.`)) return;
    setClearing(true);
    try {
      const response = await axios.delete(`${API_URL}/suppression/clear-all`);
      const sg = response.data.sendgrid_cleared;
      let msg = response.data.message;
      if (sg && (sg.bounces || sg.blocks || sg.invalid)) {
        msg += ' + cleared SendGrid bounce/block lists';
      }
      toast.success(msg);
      fetchSuppression();
    } catch (error) {
      toast.error('Failed to clear suppression list');
    } finally {
      setClearing(false);
    }
  };

  const exportCSV = () => {
    const csv = 'Email,Reason,Added At\n' + 
      suppressed.map(s => `${s.email},${s.reason},${s.created_at}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'suppression_list.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredSuppressed = suppressed.filter(s => 
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="suppression-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Suppression List</h1>
          <p className="text-muted-foreground mt-1">
            {suppressed.length} emails excluded from campaigns
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {suppressed.length > 0 && (
            <Button
              variant="outline"
              onClick={handleClearAll}
              disabled={clearing}
              className="text-destructive hover:text-destructive"
              data-testid="clear-all-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {clearing ? 'Clearing...' : 'Clear All'}
            </Button>
          )}
          <Button variant="outline" onClick={exportCSV} disabled={suppressed.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-suppression-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Email
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add to Suppression List</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="user@example.com"
                    data-testid="suppression-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason">Reason</Label>
                  <select
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="suppression-reason-select"
                  >
                    <option value="manual">Manual</option>
                    <option value="unsubscribed">Unsubscribed</option>
                    <option value="invalid">Invalid Email</option>
                    <option value="spam_trap">Spam Trap</option>
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleAdd} data-testid="save-suppression-btn">Add to List</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="border-amber-500/50 bg-amber-500/5">
        <CardContent className="flex items-start gap-3 pt-6">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Automatic Suppression</p>
            <p className="text-sm text-muted-foreground">
              Bounced emails are automatically added here via SendGrid webhooks. 
              Use "Clear All" to reset and retry sending to all addresses.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      {suppressed.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 max-w-sm"
            data-testid="suppression-search"
          />
        </div>
      )}

      {/* Suppression Table */}
      {suppressed.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSuppressed.map((item) => (
                  <TableRow key={item.id} data-testid={`suppression-${item.id}`}>
                    <TableCell className="font-medium">{item.email}</TableCell>
                    <TableCell>
                      <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs">
                        {item.reason}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(item.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(item.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`remove-suppression-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Ban className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No suppressed emails</h3>
            <p className="text-muted-foreground text-sm">
              Bounced and complained emails will appear here automatically
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Suppression;
